import {useEffect, useRef, useState} from "react";
import Select, {components} from "react-select";
import styled from "styled-components";
import {defaultThemeColors, fonts} from "ui/theme";
import {HelperText} from "../helperText/HelperText";
import {customStyles} from "./selectStyle";

const Input = (props) => <components.Input {...props} isHidden={false}/>;

const customFilter = (option, inputValue) => {
    if (inputValue && option) {
        const lowercasedInput = inputValue.toLowerCase();
        const lowercasedLabel = option.label.toLowerCase();
        return lowercasedLabel.includes(lowercasedInput);
    }
    return true;
};

const customSort = (a, b, inputValue) => {
    const lowercasedInput = inputValue.toLowerCase();
    const aStartsWithInput = a.label.toLowerCase().startsWith(lowercasedInput);
    const bStartsWithInput = b.label.toLowerCase().startsWith(lowercasedInput);

    if (aStartsWithInput && !bStartsWithInput) return -1;
    if (!aStartsWithInput && bStartsWithInput) return 1;
    return a.label.localeCompare(b.label);
};

export const SelectFilter = ({
                                 options,
                                 value,
                                 onChange,
                                 placeholder,
                                 animatedPlaceholder,
                                 label,
                                 errorText,
                                 isError,
                                 controlledMenu = true,
                                 showIndicator = false,
                                 bgColor,
                                 borderColor,
                                 rounded,
                                 onFocus,
                                 onBlur,
                                 ...rest
                             }) => {
    const [inputValue, setInputValue] = useState(value?.label || "")
    const [menuIsOpen, setMenuIsOpen] = useState(false)
    const [focus, setFocus] = useState(false)
    const [hasUserInput, setHasUserInput] = useState(false)

    const effectivePlaceholder = !focus && !hasUserInput && animatedPlaceholder ? animatedPlaceholder : placeholder

    const selectRef = useRef(null)
    // A tap on an option can refocus the input on mobile; don't reopen the menu then.
    const justSelected = useRef(false)

    const handleChange = (option) => {
        onChange(option)
        setInputValue(option ? option.label : "")
        setHasUserInput(!!option?.label)
        if (option?.label) {
            // Chosen: close the list (and the mobile keyboard). Tapping the field reopens it.
            setMenuIsOpen(false)
            justSelected.current = true
            setTimeout(() => { justSelected.current = false }, 300)
            selectRef.current?.blur()
        }
    }

    const handleInputChange = (newValue, {action}) => {
        if (action === "input-change") {
            setInputValue(newValue)
            setHasUserInput(newValue.length > 0)

            if (newValue) {
                const hasMatchingOptions = options?.some(option =>
                    customFilter(option, newValue)
                )

                if (hasMatchingOptions) {
                    setMenuIsOpen(true)
                } else {
                    // Check if input exactly matches an allowed class (case-insensitive)
                    const exactMatch = options?.find(option =>
                        option.label.toLowerCase() === newValue.toLowerCase()
                    )
                    
                    if (exactMatch) {
                        handleChange(exactMatch)
                    }
                    setMenuIsOpen(false)
                }
            } else {
                handleChange({value: "", label: ""})
                setMenuIsOpen(focus)
            }
        }
    }

    const handleFocus = () => {
        setFocus(true)
        if (!justSelected.current) setMenuIsOpen(true)  // Show all options on focus
        onFocus?.()
    }

    const handleBlur = () => {
        setFocus(false)
        onBlur?.()
    }

    useEffect(() => {
        if (value) {
            setInputValue(value.label)
            setHasUserInput(true)
        } else {
            setHasUserInput(false)
        }
    }, [value])


    useEffect(() => {
        if (!value && inputValue === "") {
            setHasUserInput(false)
        }
    }, [value, inputValue])

    return (
        <SelectContainer>
            {label ? <HelperText>{label}</HelperText> : null}
            <InputContainer
                isError={isError}
                focus={focus}
                showIndicator={showIndicator}
                bgColor={bgColor}
                borderColor={borderColor}
                rounded={rounded}
            >
                <Select
                    ref={selectRef}
                    value={value}
                    onChange={handleChange}
                    options={
                        options
                            ?.filter((option) => customFilter(option, inputValue))
                            ?.sort((a, b) => customSort(a, b, inputValue)) || []
                    }
                    placeholder={effectivePlaceholder}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    menuIsOpen={controlledMenu ? menuIsOpen : undefined}
                    onMenuClose={() => setMenuIsOpen(false)}
                    onMenuOpen={() => { if (!justSelected.current) setMenuIsOpen(true) }}
                    filterOption={null}
                    className="auto-complete-container"
                    classNamePrefix="auto-complete"
                    styles={{
                        ...customStyles,
                        control: () => null,
                        SelectContainer: () => null,
                        Input: () => null,
                    }}
                    components={{Input}}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    {...rest}
                />
            </InputContainer>

            {isError ? (
                <HelperText color="pink" size="small">
                    {errorText}
                </HelperText>
            ) : null}
        </SelectContainer>
    );
};

const SelectContainer = styled.div`
    width: 100%;
    display: grid;
    gap: 1rem;
`;

export const InputContainer = styled.label`
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 40px;
    font-size: var(--auto-complete-font-size, ${fonts.sizes.regular});
    background-color: ${({bgColor = "inputBgNeutral"}) => defaultThemeColors[bgColor]};
    border-width: 2px;
    border-style: solid;
    border-color: ${({isError, borderColor}) =>
            isError
                    ? defaultThemeColors.pink
                    : defaultThemeColors[borderColor ? borderColor : "transparent"]};
    border-radius: ${({rounded}) => (rounded ? "99px" : "5px")};
    padding-left: 1rem;
    padding-right: ${({showIndicator}) => (showIndicator ? "2rem" : "1rem")};

    & .auto-complete-container,
    .auto-complete__value-container,
    .auto-complete__control {
        width: 100%;
        height: 100%;
        padding: 0px;
        margin: 0px;
        border-radius: 8px;
    }

    & .auto-complete__control {
        display: flex;
        align-items: center;
        position: relative;
    }

    & .auto-complete__indicator {
        display: ${({showIndicator}) => (showIndicator ? "flex" : "none")};
        position: absolute;
        right: -2rem;
    }

    & .auto-complete__indicator-separator {
        display: none;
    }

    & .auto-complete__placeholder {
        color: ${defaultThemeColors.txt};
        font-size: inherit;
        overflow: hidden;
        white-space: nowrap;
    }

    & .auto-complete__single-value {
        color: ${({color = "txt"}) => defaultThemeColors[color]};
        font-size: inherit;
        font-weight: 400;
        overflow: hidden;
    }

    .auto-complete__input-container {
        color: ${defaultThemeColors.txt};
        overflow: hidden;

        .auto-complete__input {
            width: 100%;
            height: 100%;
            font-size: var(--auto-complete-font-size, ${fonts.sizes.regular}) !important;
        }
    }
`;

